import Dialog from "@components/ui/dialog";
import { SlidesLibraryDialog } from "@modules/slides-library/shared/components/slides-library-dialog";
import { useAppDispatch, useAppSelector } from "@store";
import { openDialog } from "@store/dialogs";
import { selectIsAnyDialogOpen } from "@store/dialogs/selectors";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { FunnelCloseButton } from "./components/funnel-close-button";
import { useCompleteFunnel } from "./hooks/use-complete-funnel";
import { usePlatformFunnelStore } from "./platform-funnel.store";

// Lazy so the funnel's step graph (incl. the editor design-system FontSelector
// and its FontsDialog) stays out of the always-loaded base-layout chunk; it
// fetches on first open.
const Funnel = lazy(() => import("./funnel"));

/**
 * Overlay host for the logged-in funnel. Lives in the dashboard (protected)
 * subtree so useCreateStory/account providers are available.
 *
 * Only reads `isOpen` here and returns null when closed, so the funnel's own
 * hooks/providers (useCreateStory, dialogs, router) aren't mounted at all on
 * every other page — mounting them is cost we only want to pay while the
 * overlay is actually in use. Trade-off: the shared Dialog's exit transition
 * is lost (instant unmount instead of the usual 200ms fade-out) since Inner
 * disappears immediately rather than animating out.
 */
const PlatformFunnelOverlay = () => {
  const isOpen = usePlatformFunnelStore((s) => s.isOpen);
  if (!isOpen) return null;
  return <PlatformFunnelOverlayInner />;
};

const PlatformFunnelOverlayInner = () => {
  const isOpen = usePlatformFunnelStore((s) => s.isOpen);
  const initialPath = usePlatformFunnelStore((s) => s.initialPath);
  const initialState = usePlatformFunnelStore((s) => s.initialState);
  const target = usePlatformFunnelStore((s) => s.target);
  const closeFunnel = usePlatformFunnelStore((s) => s.closeFunnel);
  const dispatch = useAppDispatch();
  // A dialog opened from inside the funnel (the template preview) renders at
  // z-10000, below this button's z-10001 — leaving the funnel's X floating over
  // it, where clicking it would discard the whole flow instead of closing the
  // preview. Hide it while anything is stacked on top.
  const isDialogOpen = useAppSelector(selectIsAnyDialogOpen);
  const { completeFunnel, cancelCompletion } = useCompleteFunnel();

  // Every path that closes the funnel must also invalidate an in-flight
  // completion, or a stale run could still navigate/close after the user
  // has already left.
  const handleCloseFunnel = useCallback(() => {
    cancelCompletion();
    closeFunnel();
  }, [cancelCompletion, closeFunnel]);

  // The shared Dialog disables floating-ui's dismiss, so wire Escape ourselves.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // A dialog is stacked on top (template preview) — Escape belongs to it,
      // same as the hidden close button while isDialogOpen.
      if (isDialogOpen) return;
      event.stopPropagation();
      handleCloseFunnel();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, isDialogOpen, handleCloseFunnel]);

  // "Start with slides" — same modal as adding a slide in a story (Saved
  // slides default). The library makes what the funnel was opened to make.
  const handleSlides = useCallback(() => {
    handleCloseFunnel();
    dispatch(
      openDialog({
        id: SlidesLibraryDialog.id,
        content: SlidesLibraryDialog,
        props: { view: "library", initialTab: "saved", createTarget: target },
      }),
    );
  }, [handleCloseFunnel, dispatch, target]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFunnel();
      }}
      closeOnOutsideClick
    >
      <Dialog.Content
        aria-label="How would you like to start?"
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Escape") {
            if (isDialogOpen) return;
            handleCloseFunnel();
          }
        }}
        overlayClassName="tw-bg-black/80 tw-backdrop-blur-[2px] tw-items-start"
        className="tw-flex tw-h-screen tw-w-screen tw-items-start tw-justify-center tw-overflow-y-auto"
      >
        {/* Portaled to body so it stays viewport-fixed while content scrolls. */}
        {!isDialogOpen && <FunnelCloseButton onClick={handleCloseFunnel} />}
        {/* Top-aligned like signup FunnelShell (pt-20); avoids vertically centering tall steps like templates. */}
        <div className="tw-flex tw-w-full tw-justify-center tw-px-4 tw-pb-10 tw-pt-20">
          <Suspense fallback={null}>
            <Funnel
              mode="platform"
              target={target}
              initialPath={initialPath}
              initialState={initialState}
              onClose={handleCloseFunnel}
              onSlides={handleSlides}
              onComplete={completeFunnel}
            />
          </Suspense>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default PlatformFunnelOverlay;
